import { useEffect, useState, useCallback } from "react";
import { Plus, ChevronLeft, ChevronRight, Loader2, Video, Phone, Building2, Monitor } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";
import { analytics } from "@/services/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";

const PAGE_SIZE = 20;
const MEETING_MODES = [
  { value: "google_meet", label: "Google Meet", icon: Video },
  { value: "zoom", label: "Zoom", icon: Video },
  { value: "teams", label: "MS Teams", icon: Monitor },
  { value: "phone", label: "Phone Call", icon: Phone },
  { value: "office", label: "Office Visit", icon: Building2 },
];

const EMPTY_FORM = {
  user_id: "", advisor_id: "", consultation_id: "",
  scheduled_at: "", duration_mins: 30, meeting_mode: "google_meet",
  meeting_link: "", notes: "",
};

export default function AdminAppointments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [advisors, setAdvisors] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/appointments", { params });
      setItems(data.appointments);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  useEffect(() => {
    api.get("/advisors", { params: { page_size: 100 } })
      .then(r => setAdvisors(r.data.advisors || []))
      .catch(() => {});
  }, []);

  const createAppointment = async () => {
    if (!form.user_id || !form.advisor_id || !form.scheduled_at) {
      toast.error("User, advisor and date/time are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", { ...form, duration_mins: Number(form.duration_mins) });
      analytics.appointmentBook(form.meeting_mode);
      toast.success("Appointment created");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      loadAppointments();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader
          title="Appointments"
          subtitle={`${total} total appointments`}
          actions={
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 rounded-full h-9 px-5 font-heading uppercase tracking-wider text-xs">
                  <Plus size={13} className="mr-1.5" /> New Appointment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-[hsl(var(--zt-teal))]">New Appointment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                  {[
                    { key: "user_id", label: "User ID *", type: "text" },
                    { key: "consultation_id", label: "Consultation ID (optional)", type: "text" },
                    { key: "scheduled_at", label: "Date & Time *", type: "datetime-local" },
                    { key: "duration_mins", label: "Duration (minutes)", type: "number" },
                    { key: "meeting_link", label: "Meeting Link", type: "url" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">{f.label}</label>
                      <input
                        type={f.type}
                        value={form[f.key]}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30"
                      />
                    </div>
                  ))}

                  <div>
                    <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">Advisor *</label>
                    <select value={form.advisor_id} onChange={e => setForm(p => ({ ...p, advisor_id: e.target.value }))}
                      className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30">
                      <option value="">Select advisor…</option>
                      {advisors.map(a => <option key={a.advisor_id} value={a.advisor_id}>{a.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">Meeting Mode</label>
                    <select value={form.meeting_mode} onChange={e => setForm(p => ({ ...p, meeting_mode: e.target.value }))}
                      className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30">
                      {MEETING_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">Notes</label>
                    <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full h-16 px-3 py-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none resize-none" />
                  </div>

                  <Button onClick={createAppointment} disabled={saving} className="w-full bg-[hsl(var(--zt-teal))] text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : "Create Appointment"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          }
        />

        {/* Filter */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 shadow-sm">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider">
            <option value="">All Statuses</option>
            {["scheduled", "confirmed", "completed", "cancelled", "rescheduled"].map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead>
                <tr className="border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                  {["User", "Advisor", "Date & Time", "Mode", "Status", "Meeting Link"].map(h => (
                    <th key={h} className="px-4 py-3.5 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 animate-pulse">
                      {[...Array(6)].map((_, j) => <td key={j} className="px-4 py-4"><div className="h-4 bg-[hsl(var(--zt-beige-deep))]/50 rounded" /></td>)}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">No appointments found</td></tr>
                ) : items.map(a => (
                  <tr key={a.appointment_id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{a.user_name || "—"}</p>
                      <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{a.user_email}</p>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/70">{a.advisor_name || "—"}</td>
                    <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/70 whitespace-nowrap">{fmtDateTime(a.scheduled_at)}</td>
                    <td className="px-4 py-3.5">
                      <ModeChip mode={a.meeting_mode} />
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3.5">
                      {a.meeting_link ? (
                        <a href={a.meeting_link} target="_blank" rel="noopener noreferrer"
                          className="text-[11px] text-[hsl(var(--zt-gold))] hover:underline font-heading uppercase tracking-wider">Join</a>
                      ) : <span className="text-[11px] text-[hsl(var(--zt-charcoal))]/30">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--zt-beige-deep))]">
              <p className="text-xs text-[hsl(var(--zt-charcoal))]/50 font-heading">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function ModeChip({ mode }) {
  const labels = { google_meet: "Google Meet", zoom: "Zoom", teams: "Teams", phone: "Phone", office: "Office" };
  return (
    <span className="inline-flex items-center text-[9px] font-heading uppercase tracking-wider bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-charcoal))]/60 px-2 py-0.5 rounded-full">
      {labels[mode] || mode}
    </span>
  );
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
